import type { CSSProperties, ReactNode } from 'react';
import styles from './Viz.module.css';

export type VizTone =
  'main' | 'maroon' | 'err' | 'warn' | 'ok' | 'cur' | 'blush' | 'gray';

/** Pipeline of stages (prototype .flow); `hot` marks the stage needing work. */
export function Flow({
  steps,
}: {
  steps: {
    label: string;
    value: ReactNode;
    caption?: ReactNode;
    hot?: boolean;
    zero?: boolean;
  }[];
}) {
  return (
    <div
      className={styles.flow}
      style={{ '--n': steps.length } as CSSProperties}
    >
      {steps.map((s) => (
        <div
          key={s.label}
          className={[
            styles.step,
            s.hot ? styles.hot : '',
            s.zero ? styles.zero : '',
          ]
            .filter(Boolean)
            .join(' ')}
        >
          <small>{s.label}</small>
          <b>{s.value}</b>
          {s.caption && <span>{s.caption}</span>}
        </div>
      ))}
    </div>
  );
}

/** Horizontal bars against a shared maximum (prototype .hbars). */
export function HBars({
  rows,
  total,
}: {
  rows: {
    label: ReactNode;
    value: number;
    display: ReactNode;
    tone?: VizTone;
    extra?: ReactNode;
  }[];
  total?: { label: string; display: ReactNode };
}) {
  const max = Math.max(1, ...rows.map((r) => r.value));
  return (
    <div className={styles.hbars}>
      {rows.map((r, i) => (
        <div key={i} className={styles.hb}>
          <div className={styles.hbHead}>
            <b>{r.label}</b>
            {r.extra}
            <span className={styles.hbValue}>{r.display}</span>
          </div>
          <div className={styles.track} aria-hidden="true">
            <i
              className={styles[r.tone ?? 'main']}
              style={{ width: `${(100 * r.value) / max}%` }}
            />
          </div>
        </div>
      ))}
      {total && (
        <div className={styles.total}>
          <span>{total.label}</span>
          <span>{total.display}</span>
        </div>
      )}
    </div>
  );
}

/** Label / big value / caption rows (prototype .gstat). */
export function GStats({
  rows,
}: {
  rows: { label: ReactNode; value: ReactNode; caption?: ReactNode }[];
}) {
  return (
    <div>
      {rows.map((r, i) => (
        <div key={i} className={styles.gstat}>
          <div className={styles.gstatRow}>
            <b>{r.label}</b>
            <span className={styles.gstatValue}>{r.value}</span>
          </div>
          {r.caption && <span>{r.caption}</span>}
        </div>
      ))}
    </div>
  );
}
